import React, { useState } from "react";
import { Alert, Dropdown, Spinner } from "react-bootstrap";
import {
  useEticketingCreateTimingsMutation,
  useEticketingUpdateTimingMutation,
  useEticketingDeleteTimingMutation,
} from "../../../../api/e-ticketing/e-ticketing.hooks";
import { emojis } from "../../bootstrap/alertData";
import DeleteModal from "../../DeleteModal";
import { Can } from "../../../../context/Can";

const RouteTimingForm = ({ timings, routeId, onRefresh }) => {
  const { create: createTiming, isLoading: creating } =
    useEticketingCreateTimingsMutation();
  const { update: updateTiming, isLoading: updating } =
    useEticketingUpdateTimingMutation();
  const { remove: deleteTiming, isLoading: deleting } =
    useEticketingDeleteTimingMutation();

  const [showForm, setShowForm] = useState(false);
  const [editingTiming, setEditingTiming] = useState(null);
  const [formData, setFormData] = useState({
    time_from: "",
    time_to: "",
  });
  const [alert, setAlert] = useState({ show: false, message: "", variant: "" });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [timingToDelete, setTimingToDelete] = useState(null);

  const isEditMode = !!editingTiming;

  const showAlert = (message, variant = "success") => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert({ show: false, message: "", variant: "" }), 3000);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddNew = () => {
    setEditingTiming(null);
    setFormData({ time_from: "", time_to: "" });
    setShowForm(true);
  };

  const handleEdit = (timing) => {
    setEditingTiming(timing);
    setFormData({
      time_from: timing.time_from || "",
      time_to: timing.time_to || "",
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingTiming(null);
    setFormData({ time_from: "", time_to: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const submitData = {
      ...formData,
      routeId: routeId,
    };

    try {
      if (isEditMode) {
        await updateTiming(editingTiming.id, submitData);
        showAlert("Timing updated successfully!");
      } else {
        await createTiming(submitData);
        showAlert("Timing added successfully!");
      }
      onRefresh();
      handleCancel();
    } catch (err) {
      showAlert(
        err?.message || `Failed to ${isEditMode ? "update" : "create"} timing.`,
        "danger"
      );
    }
  };

  const handleDeleteClick = (timing) => {
    setTimingToDelete(timing);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!timingToDelete) return;

    try {
      await deleteTiming(timingToDelete.id);
      showAlert("Timing deleted successfully!");
      onRefresh();
      setShowDeleteModal(false);
      setTimingToDelete(null);
    } catch (err) {
      showAlert(err?.message || "Failed to delete timing.", "danger");
    }
  };

  return (
    <div>
      {alert.show && (
        <Alert
          variant={alert.variant}
          dismissible
          onClose={() => setAlert({ show: false, message: "", variant: "" })}
        >
          {alert.variant === "success" ? emojis.success : emojis.warning}{" "}
          {alert.message}
        </Alert>
      )}

      {showForm ? (
        <div className="card">
          <div className="card-header">
            <h5 className="card-title m-0">
              {isEditMode ? "Edit Timing" : "Add Timing"}
            </h5>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-xl-6">
                  <div className="mb-3 row">
                    <label className="col-lg-4 col-form-label">
                      Time From<span className="text-danger">*</span>
                    </label>
                    <div className="col-lg-6">
                      <input
                        type="time"
                        name="time_from"
                        value={formData.time_from}
                        onChange={handleFormChange}
                        className="form-control"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="col-xl-6">
                  <div className="mb-3 row">
                    <label className="col-lg-4 col-form-label">
                      Time To<span className="text-danger">*</span>
                    </label>
                    <div className="col-lg-6">
                      <input
                        type="time"
                        name="time_to"
                        value={formData.time_to}
                        onChange={handleFormChange}
                        className="form-control"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="mb-3 row">
                  <div className="col-lg-10 ms-auto">
                    <button
                      type="submit"
                      className="btn btn-secondary"
                      disabled={creating || updating}
                    >
                      {creating || updating ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          {isEditMode ? "Updating..." : "Creating..."}
                        </>
                      ) : (
                        "Save"
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-primary ms-2"
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title m-0">Route Timings</h5>
            <Can I="create" a="Route Timing">
              <button
                className="btn btn-secondary btn-sm"
                onClick={handleAddNew}
              >
                <i className="fa fa-plus me-1"></i> Add Timing
              </button>
            </Can>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Time From</th>
                    <th>Time To</th>
                    <th>Created At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {timings.length > 0 ? (
                    timings.map((timing, index) => (
                      <tr key={timing.id}>
                        <td>{index + 1}</td>
                        <td>
                          <span className="badge bg-primary">
                            {timing.time_from || "--"}
                          </span>
                        </td>
                        <td>
                          <span className="badge bg-success">
                            {timing.time_to || "--"}
                          </span>
                        </td>
                        <td>
                          {timing.createdAt
                            ? new Date(timing.createdAt).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                }
                              )
                            : "--"}
                        </td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle variant="link">
                              &#x2022;&#x2022;&#x2022;
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end">
                              <Dropdown.Item onClick={() => handleEdit(timing)}>
                                Edit
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() => handleDeleteClick(timing)}
                              >
                                Delete
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center">
                        No timings found for this route.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <DeleteModal
        show={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setTimingToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Timing"
        message={`Are you sure you want to delete this timing (${timingToDelete?.time_from} - ${timingToDelete?.time_to})? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={deleting}
        danger={true}
      />
    </div>
  );
};

export default RouteTimingForm;
